import * as t from 'io-ts';

import {RConversationTimelineRange} from '../../../helpers/viewerHelpers';
import {RActivityIdRef} from '../../../models/api/activityModel';
import {RCommentUidRef, RConversationIdRef} from '../../../models/api/apiTypes';
import {makePermanentResourceMap, makeResourceMap, RAsyncMoreStatus, RAsyncStatus} from '../../appTypes';
import {RCellsContext} from '../cells/context/cellsContext';
import {RInboxesParameters} from '../inboxes/parameters/inboxesParameters';
import {RPanelCancelTokenId} from '../panelCancelTokenId';
import {PanelTypesEnum, RPanelStateBase, RViewerPanelId} from '../panelStateBase';
import {RActivityViewerState} from './activity/activityViewerState';

/*
 * Runtime type.
 */

export enum ViewerPanelPageTypesEnum {
  NO_CONVERSATION = 'NO_CONVERSATION',
  SINGLE_CONVERSATION = 'SINGLE_CONVERSATION',
  MULTI_CONVERSATIONS = 'MULTI_CONVERSATIONS',
}

const RViewerPanelPageStateBase = t.type({
  uid: t.string,
  parameters: RInboxesParameters,
  cellsContext: RCellsContext,
});

const RViewerPanelEmptyPageState = t.intersection([
  RViewerPanelPageStateBase,
  t.type({
    type: t.literal(ViewerPanelPageTypesEnum.NO_CONVERSATION),
  }),
]);

const RViewerPanelConversationState = t.type({
  lastActivityDate: t.number,
  seenAt: t.union([t.number, t.undefined]),
  autoSeenAt: t.union([t.number, t.undefined]),
});

const RViewerPanelConversationPageState = t.intersection([
  RViewerPanelPageStateBase,
  RConversationTimelineRange,
  t.type({
    uid: t.string,
    type: t.literal(ViewerPanelPageTypesEnum.SINGLE_CONVERSATION),
    conversationId: RConversationIdRef,
    status: RAsyncStatus,
    activities: makePermanentResourceMap(t.Integer, RActivityViewerState),
    draftMessageSequences: makeResourceMap(t.string, t.Integer),
    moreStatusTop: RAsyncMoreStatus,
    moreStatusBottom: RAsyncMoreStatus,
    /** Whether "Expand all" is active and content should be loaded expanded. */
    shouldExpandMessages: t.boolean,
  }),
  t.partial({
    /** Unique identifier to help jump to the same activity more than once in a row. */
    jumpUid: t.string,
    /** ID of the activity that initiated the jump, if any. This is typically a comment with a reply/quote. */
    jumpSourceActivityId: RActivityIdRef,
    /** State of the conversation when the user navigated to it. */
    originalConversationState: RViewerPanelConversationState,
    editingCommentUid: RCommentUidRef,
  }),
]);

const RViewerPanelMultiPageState = t.intersection([
  RViewerPanelPageStateBase,
  t.type({
    type: t.literal(ViewerPanelPageTypesEnum.MULTI_CONVERSATIONS),
  }),
]);

const RViewerPanelPageState = t.taggedUnion('type', [
  RViewerPanelEmptyPageState,
  RViewerPanelConversationPageState,
  RViewerPanelMultiPageState,
]);

const RViewerPanelPagesState = t.type({
  current: RViewerPanelPageState,
  next: t.union([t.undefined, RViewerPanelPageState]),
});

export const RViewerPanelState = t.intersection([
  RPanelStateBase,
  t.type({
    id: RViewerPanelId,
    type: t.literal(PanelTypesEnum.VIEWER),
    pages: RViewerPanelPagesState,
    cancelTokenId: t.union([t.undefined, RPanelCancelTokenId]),
    composeCommentUid: t.union([t.undefined, RCommentUidRef]),
  }),
]);

/*
 * Types.
 */

export interface ViewerPanelEmptyPageState extends Readonly<t.TypeOf<typeof RViewerPanelEmptyPageState>> {}
export interface ViewerPanelConversationState
  extends Readonly<t.TypeOf<typeof RViewerPanelConversationState>> {}
export interface ViewerPanelConversationPageState
  extends Readonly<t.TypeOf<typeof RViewerPanelConversationPageState>> {}
export interface ViewerPanelMultiPageState extends Readonly<t.TypeOf<typeof RViewerPanelMultiPageState>> {}
export type ViewerPanelPageState =
  | ViewerPanelEmptyPageState
  | ViewerPanelConversationPageState
  | ViewerPanelMultiPageState;

export interface ViewerPanelPagesState extends Readonly<t.TypeOf<typeof RViewerPanelPagesState>> {}
export interface ViewerPanelState extends Readonly<t.TypeOf<typeof RViewerPanelState>> {}
